import React from 'react'
import { motion, useMotionValue, useSpring } from 'framer-motion'
import { TrendingUp, TrendingDown, PieChart, DollarSign, RefreshCw, BarChart3, ExternalLink } from 'lucide-react'
import { LineChart, Line, ResponsiveContainer } from 'recharts'
import { VirtualPortfolio, VirtualHolding } from '../../types/investment'

interface PortfolioPanelProps {
  portfolio: VirtualPortfolio | null
  holdings: VirtualHolding[]
  totalValue: number
  totalGainLoss: number
  totalGainLossPercent: number
  loading: boolean
  onRefresh: () => void
}

const PortfolioPanel: React.FC<PortfolioPanelProps> = ({
  portfolio,
  holdings,
  totalValue,
  totalGainLoss,
  totalGainLossPercent,
  loading,
  onRefresh
}) => {
  const isPositive = totalGainLoss >= 0
  
  // Animated counter for total value
  const animatedValue = useMotionValue(totalValue)
  const springValue = useSpring(animatedValue, { stiffness: 100, damping: 30 })
  
  React.useEffect(() => {
    animatedValue.set(totalValue)
  }, [totalValue, animatedValue])
  
  // Generate mock sparkline data for holdings
  const generateSparklineData = (holding: VirtualHolding) => {
    const basePrice = holding.currentPrice
    const data = []
    for (let i = 0; i < 10; i++) {
      const change = (Math.random() - 0.5) * 0.1 * basePrice
      data.push({
        price: basePrice + change,
        time: i
      })
    }
    return data
  }

  // Calculate allocation data
  const allocationData = holdings.reduce((acc, holding) => {
    const category = holding.companyName || 'Stocks'
    if (!acc[category]) {
      acc[category] = { value: 0, percentage: 0, color: getRandomColor() }
    }
    acc[category].value += holding.marketValue
    return acc
  }, {} as Record<string, { value: number; percentage: number; color: string }>)

  // Calculate percentages
  Object.values(allocationData).forEach(item => {
    item.percentage = totalValue > 0 ? (item.value / totalValue) * 100 : 0
  })

  const allocationEntries = Object.entries(allocationData).sort((a, b) => b[1].value - a[1].value)

  return (
    <div className="space-y-6">
      {/* Portfolio Summary */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-background-900 to-background-800 rounded-2xl p-6 border border-background-700 shadow-xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary-500/20 rounded-lg">
              <PieChart className="w-6 h-6 text-primary-400" />
            </div>
            <h3 className="text-xl font-semibold text-background-100">Portfolio</h3>
          </div>
          <button
            onClick={onRefresh}
            disabled={loading}
            className="p-2 hover:bg-background-700 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 text-background-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Total Value */}
        <div className="text-center mb-6">
          <motion.div className="text-4xl font-bold text-background-100 mb-2">
            ${Math.round(springValue.get()).toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </motion.div>
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`flex items-center justify-center space-x-2 text-lg ${
              isPositive ? 'text-green-400' : 'text-red-400'
            }`}
          >
            {isPositive ? (
              <TrendingUp className="w-5 h-5" />
            ) : (
              <TrendingDown className="w-5 h-5" />
            )}
            <span>
              {isPositive ? '+' : ''}${totalGainLoss.toFixed(2)} ({totalGainLossPercent.toFixed(2)}%)
            </span>
          </motion.div>
          {portfolio && (
            <div className="text-background-400 text-sm mt-1">
              Started with ${portfolio.startingBalance.toLocaleString()}
            </div>
          )}
        </div>

        {/* Cash vs Holdings */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-background-800 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <DollarSign className="w-4 h-4 text-background-400" />
              <span className="text-sm text-background-400">Cash</span>
            </div>
            <div className="text-xl font-semibold text-background-100">
              ${portfolio?.currentCash.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}
            </div>
          </div>
          <div className="bg-background-800 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <PieChart className="w-4 h-4 text-background-400" />
              <span className="text-sm text-background-400">Holdings</span>
            </div>
            <div className="text-xl font-semibold text-background-100">
              ${holdings.reduce((sum, h) => sum + h.marketValue, 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </div>
          </div>
        </div>

        {/* Mode Badge */}
        {portfolio && (
          <div className="flex justify-center">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              portfolio.mode === 'beginner' ? 'bg-green-500/20 text-green-400' :
              portfolio.mode === 'intermediate' ? 'bg-blue-500/20 text-blue-400' :
              'bg-purple-500/20 text-purple-400'
            }`}>
              {portfolio.mode.charAt(0).toUpperCase() + portfolio.mode.slice(1)} Mode
            </span>
          </div>
        )}
      </motion.div>

        {/* Holdings */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-gradient-to-br from-background-900/50 to-background-800/50 backdrop-blur-sm rounded-3xl p-6 border border-background-700/50 shadow-2xl"
      >
        <h4 className="text-lg font-semibold text-background-100 mb-6">Holdings</h4>
        
        {holdings.length === 0 ? (
          <div className="text-center py-12">
            <PieChart className="w-16 h-16 text-background-600 mx-auto mb-4" />
            <p className="text-background-400 text-lg mb-2">No holdings yet</p>
            <p className="text-background-500 text-sm">Start trading to build your portfolio</p>
          </div>
        ) : (
          <div className="space-y-4">
            {holdings.map((holding, index) => {
              const sparklineData = generateSparklineData(holding)
              const isPositive = holding.gainLoss >= 0
              
              return (
                <motion.div 
                  key={holding.id} 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.02 }}
                  className="flex items-center justify-between p-4 bg-background-800/50 backdrop-blur-sm rounded-2xl border border-background-700/30 hover:border-background-600/50 transition-all duration-300"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-background-700 to-background-600 rounded-xl flex items-center justify-center shadow-lg">
                      <span className="text-sm font-bold text-background-200">
                        {holding.symbol.slice(0, 2)}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-background-100">
                        {holding.symbol}
                      </div>
                      <div className="text-xs text-background-400">
                        {holding.shares} shares
                      </div>
                    </div>
                  </div>
                  
                  {/* Mini Chart */}
                  <div className="w-20 h-12 mr-4">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={sparklineData}>
                        <Line 
                          type="monotone" 
                          dataKey="price" 
                          stroke={isPositive ? '#10B981' : '#EF4444'}
                          strokeWidth={2}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  
                  <div className="text-right">
                    <div className="text-sm font-semibold text-background-100">
                      ${holding.marketValue.toFixed(2)}
                    </div>
                    <div className={`text-xs font-medium ${
                      isPositive ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {isPositive ? '+' : ''}${holding.gainLoss.toFixed(2)} ({holding.gainLossPercent.toFixed(2)}%)
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </div>
        )}
      </motion.div>

      {/* Allocation Chart */}
      {allocationEntries.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-background-900/50 to-background-800/50 backdrop-blur-sm rounded-3xl p-6 border border-background-700/50 shadow-2xl"
        >
          <h4 className="text-lg font-semibold text-background-100 mb-6">Asset Allocation</h4>
          <div className="space-y-4">
            {allocationEntries.map(([category, data], index) => (
              <motion.div 
                key={category} 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center justify-between"
              >
                <div className="flex items-center space-x-3">
                  <div 
                    className="w-4 h-4 rounded-full shadow-sm"
                    style={{ backgroundColor: data.color }}
                  />
                  <span className="text-sm font-medium text-background-200">{category}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-32 bg-background-700/50 rounded-full h-2.5">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: `${data.percentage}%` }}
                      transition={{ duration: 1, delay: index * 0.2 }}
                      className="h-2.5 rounded-full shadow-sm"
                      style={{ backgroundColor: data.color }}
                    />
                  </div>
                  <span className="text-sm font-semibold text-background-100 w-16 text-right">
                    {data.percentage.toFixed(1)}%
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}
      
      {/* Research Tools Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-gradient-to-r from-primary-500/10 via-primary-600/10 to-primary-500/10 backdrop-blur-sm rounded-3xl p-6 border border-primary-500/20 shadow-xl"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-primary-500/20 rounded-xl">
              <BarChart3 className="w-6 h-6 text-primary-400" />
            </div>
            <div>
              <h4 className="text-lg font-semibold text-background-100 mb-1">
                Advanced Research Tools
              </h4>
              <p className="text-background-300 text-sm">
                Get detailed stock analysis, financial data, and market insights
              </p>
            </div>
          </div>
          <a
            href="https://simplystock.net"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-2 bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-xl font-medium transition-all duration-300 hover:scale-105 shadow-lg"
          >
            <span>Visit SimplyStock</span>
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </motion.div>
    </div>
  )
}

// Generate random colors for allocation chart
function getRandomColor(): string {
  const colors = [
    '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
    '#06B6D4', '#84CC16', '#F97316', '#EC4899', '#6366F1'
  ]
  return colors[Math.floor(Math.random() * colors.length)]
}

export default PortfolioPanel
